/**
 * Entry point for the front-end bundle.
 *
 * Boot order: locate root → wire toast → wire login modal → wire tabs →
 * wire each tab's UI.
 */

import '../styles/frontend.scss';

import { api, ApiError } from './api.js';
import { byHook } from './dom.js';
import { balanceCard } from './ui/balanceCard.js';
import { createHistoryTable } from './ui/historyTable.js';
import { createLoginModal } from './ui/loginModal.js';
import { createOrderForm } from './ui/orderForm.js';
import { createTabs } from './ui/tabs.js';
import { createToast } from './ui/toast.js';

const start = () => {
    const root = document.querySelector('[data-marc-root]');
    if (!root) return;

    const toast = createToast(root);

    const handleAuthenticated = async () => {
        // After a successful sign-in, show whatever the user was asked to
        // confirm in the gate card so they can keep going on the same page.
        const gate = byHook('login-gate', root);
        if (gate) gate.hidden = true;
        // Re-enable any inputs we disabled while logged-out.
        root.querySelectorAll('[data-marc-requires-auth]').forEach((el) => {
            el.removeAttribute('disabled');
        });

        // Fetch the current user's myCred balance + review summary and
        // render (or refresh) the balance card. The card is server-
        // rendered for sessions that started logged-in, but on AJAX login
        // the page never reloads, so without this the user has to do a
        // hard refresh to see their balance.
        try {
            const data = await api.accountSummary();
            if (data?.logged_in) {
                balanceCard.render(root, data);
            }
        } catch (error) {
            // Non-fatal — we don't want to block the auth flow if the
            // summary endpoint hiccups. The user can refresh to recover.
            if (!(error instanceof ApiError)) throw error;
        }
    };

    const loginModal = createLoginModal(root, {
        toast,
        onAuthenticated: handleAuthenticated,
    });

    const handleLoginRequired = () => {
        loginModal.open({ initialMode: 'login' });
    };

    const orderForm = createOrderForm(root, {
        toast,
        onLoginRequired: handleLoginRequired,
    });

    const history = createHistoryTable(root, {
        onResubmit(item) {
            tabs.activate('submit');
            orderForm.reset();
            orderForm.prefillOrder(item.order_number);
        },
    });

    const tabs = createTabs(root, {
        onChange(name) {
            if (name === 'history') history.load();
        },
    });

    // Login-gate CTAs (visible only while logged out).
    byHook('login-gate-login', root)?.addEventListener('click', () => {
        loginModal.open({ initialMode: 'login' });
    });
    byHook('login-gate-register', root)?.addEventListener('click', () => {
        loginModal.open({ initialMode: 'register' });
    });

    // Note: We deliberately DO NOT auto-open the modal on order-input focus.
    // Doing so created a focus → modal → close → focus-restore → modal loop
    // (the modal's focus-trap restores focus to the previously-focused
    // element on close, which would re-fire the focus handler). The
    // "Query Order" button already short-circuits to onLoginRequired() when
    // the user actually tries to do something, which is enough.

    // No history fetch on initial paint; defer until the user opens the tab.
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
} else {
    start();
}
